import React from "react";
import LegalLayout from "@/components/LegalLayout";
import { HOMESTAY } from "@/lib/data";

export default function RefundPolicyPage() {
  React.useEffect(() => {
    document.title = "Refund Policy | Mrinmaya vacation home";
  }, []);

  return (
    <LegalLayout
      eyebrow="Refunds"
      title="Cancellation & Refund Policy"
      testId="refund-policy-page"
    >
      <p>
        We understand that plans can change. Our refund policy is designed to be
        straightforward and fair — to you, and to other guests waiting for the
        same dates.
      </p>

      <div
        className="mt-8 rounded-2xl border-l-4 border-mrn-terracotta bg-white/70 p-6 md:p-8"
        data-testid="refund-summary"
      >
        <h2 className="font-display text-2xl md:text-3xl text-mrn-wood">
          The short version
        </h2>
        <ul className="mt-4 space-y-3 text-mrn-wood-soft">
          <li>
            <strong className="text-mrn-wood">Cancel 7+ days before check-in:</strong>{" "}
            full refund of the booking amount.
          </li>
          <li>
            <strong className="text-mrn-wood">Cancel within 7 days of check-in:</strong>{" "}
            the complete booking amount will be charged; no refund will be
            issued.
          </li>
          <li>
            <strong className="text-mrn-wood">Once approved:</strong> the refund
            is credited to the original payment method within{" "}
            <strong>7–10 business days</strong>.
          </li>
        </ul>
      </div>

      <section className="mt-12">
        <h2 className="font-display text-2xl md:text-3xl text-mrn-wood">
          Cancellation window
        </h2>
        <p className="mt-3">
          Refunds are only issued for cancellations made at least{" "}
          <strong>seven (7) days before</strong> your scheduled check-in date,
          calculated from the check-in time (2:00 PM IST). Any cancellation
          made within seven days of check-in will be charged the full booking
          amount, and no refund will be issued.
        </p>
      </section>

      <section className="mt-10">
        <h2 className="font-display text-2xl md:text-3xl text-mrn-wood">
          How to cancel
        </h2>
        <p className="mt-3">
          To cancel a booking, please contact us by email or WhatsApp using the
          details below. The cancellation is considered effective from the time
          we acknowledge receipt of your request.
        </p>
        <ul className="mt-4 list-disc pl-5 space-y-1.5">
          <li>
            Email:{" "}
            <a
              href={`mailto:${HOMESTAY.contact.email}`}
              className="text-mrn-terracotta link-underline"
            >
              {HOMESTAY.contact.email}
            </a>
          </li>
          <li>
            Phone / WhatsApp:{" "}
            <a
              href={`tel:${HOMESTAY.contact.phone}`}
              className="text-mrn-terracotta link-underline"
            >
              {HOMESTAY.contact.phoneDisplay}
            </a>
          </li>
        </ul>
      </section>

      <section className="mt-10">
        <h2 className="font-display text-2xl md:text-3xl text-mrn-wood">
          Refund timeline
        </h2>
        <p className="mt-3">
          Once a cancellation is approved, the refund will be processed to the
          original payment method (UPI, card, bank transfer, etc.) within{" "}
          <strong>7–10 business days</strong>. The exact time taken to reflect
          in your account may depend on your bank or payment provider.
        </p>
      </section>

      <section className="mt-10">
        <h2 className="font-display text-2xl md:text-3xl text-mrn-wood">
          No-shows & early check-out
        </h2>
        <p className="mt-3">
          No-shows on the scheduled check-in date will be treated as a
          same-day cancellation and charged the full booking amount. Early
          check-outs by the guest do not qualify for a partial refund.
        </p>
      </section>

      <section className="mt-10">
        <h2 className="font-display text-2xl md:text-3xl text-mrn-wood">
          Force majeure
        </h2>
        <p className="mt-3">
          In rare situations — such as natural disasters, government-mandated
          shutdowns, or other circumstances beyond either party's control —
          we will do our best to offer a credit toward a future stay in place
          of a refund. These cases are handled on a good-faith basis.
        </p>
      </section>

      <section className="mt-10">
        <h2 className="font-display text-2xl md:text-3xl text-mrn-wood">
          Reservations made via Airbnb
        </h2>
        <p className="mt-3">
          Bookings made through Airbnb follow Airbnb's own cancellation policy
          shown at the time of booking. The terms above apply only to direct
          bookings made through this website or via our contact channels.
        </p>
      </section>
    </LegalLayout>
  );
}
